/**
 * UI foundation 1.0b — what a person reads when an action or a load fails.
 *
 * One table turns a failure into a sentence in the reader's language. It is
 * consulted in this order, first match wins:
 *
 *  1. the server's `details.reason` business codes with a known prefix
 *     (ADDRESS_*, ACCESS_*, SUBMISSION_*, plus ORDER_SITE_REQUIRED and
 *     PROFORMA_ISSUED, ORDER_CANCEL_*, the worker-report refusals) — see
 *     functions/src/geo/resolveAddress.ts, sitePermissions / siteAuthority,
 *     submission guard, timesheets/reportWorkException.ts;
 *  2. the worker/timesheet codes (`details.reason`, `details.conflict`, or a
 *     non-transport `code`) translated under `common:workerErrors.*`;
 *  3. the Firebase transport code (`functions/…`, `firestore/…`, bare) →
 *     `common:errors.<code>`;
 *  4. anything else → one generic sentence.
 *
 * The error's own `message` is NEVER returned: it is server or SDK prose in
 * English (PB-13), and a person cannot act on it. Diagnostics belong in logs.
 */
import type { TFunction } from 'i18next';

type Audience = 'client' | 'worker';

/** Refusals of `reportWorkException` (and a reused request id on `requestLeave`). */
const WORK_EXCEPTION_REASONS = new Set([
  'NOT_ON_JOB',
  'NOT_FIXED_PRICE',
  'JOB_INVOICED',
  'MISSED_DAY_IN_FUTURE',
  'CLIENT_REQUEST_ID_REUSED',
]);

/** Row 1 — business reasons whose prefix says which catalog explains them. */
const REASON_ROWS: ReadonlyArray<{
  test: (reason: string) => boolean;
  key: (reason: string) => string;
}> = [
  // Where the work happens: an hourly company order needs its object.
  {
    test: (r) => r === 'ORDER_SITE_REQUIRED',
    key: () => 'common:address.errors.ORDER_SITE_REQUIRED',
  },
  // A changed order after its proforma: cancel and order again (Marti 2026-10-03).
  {
    test: (r) => r === 'PROFORMA_ISSUED',
    key: () => 'admin:jobs.prepayCard.errors.PROFORMA_ISSUED',
  },
  // E4 — cancelling the whole order (functions/src/jobs/cancelOrder.ts).
  { test: (r) => r.startsWith('ORDER_CANCEL_'), key: (r) => `common:orderCancel.errors.${r}` },
  // Stage 1A-d — the worker's structured reports (functions/src/timesheets/reportWorkException.ts).
  {
    test: (r) => WORK_EXCEPTION_REASONS.has(r),
    key: (r) => `common:exceptions.error.${r}`,
  },
  // PB-13 — access refusals from sitePermissions / siteAuthority.
  { test: (r) => r.startsWith('ACCESS_'), key: (r) => `common:access.errors.${r}` },
  // PB-15 — the same press of Submit, answered once.
  { test: (r) => r.startsWith('SUBMISSION_'), key: (r) => `common:submission.errors.${r}` },
  { test: (r) => r.startsWith('ADDRESS_'), key: (r) => `common:address.errors.${r}` },
];

/** Row 3 — the Firebase (callable + Firestore) status codes. */
export const TRANSPORT_CODES = [
  'cancelled',
  'unknown',
  'invalid-argument',
  'deadline-exceeded',
  'not-found',
  'already-exists',
  'permission-denied',
  'resource-exhausted',
  'failed-precondition',
  'aborted',
  'out-of-range',
  'unimplemented',
  'internal',
  'unavailable',
  'data-loss',
  'unauthenticated',
] as const;
const TRANSPORT = new Set<string>(TRANSPORT_CODES);

/**
 * Transport codes whose existing `common:workerErrors` wording is neutral
 * enough for a client. Until `common:errors.*` lands in the catalogs these
 * keep the client text translated; worker-phrased ones (not-found,
 * failed-precondition) fall to the generic sentence instead.
 */
const NEUTRAL_WORKER_TRANSPORT = new Set([
  'already-exists',
  'unavailable',
  'resource-exhausted',
  'aborted',
  'permission-denied',
  'invalid-argument',
  'unauthenticated',
]);

/** A code is only ever used as a key segment — never let it walk the catalog. */
const SAFE = /^[A-Za-z0-9_-]+$/;
const safe = (value: unknown): string | undefined =>
  typeof value === 'string' && SAFE.test(value) ? value : undefined;

interface ErrorShape {
  code?: unknown;
  details?: { reason?: unknown; conflict?: unknown } | null;
}

function shape(err: unknown): ErrorShape {
  return err && typeof err === 'object' ? (err as ErrorShape) : {};
}

/** `functions/not-found`, `firestore/not-found` and `not-found` are the same code. */
function codeOf(err: unknown): string | undefined {
  const code = shape(err).code;
  return typeof code === 'string' ? safe(code.replace(/^[a-z-]+\//, '')) : undefined;
}

function generic(t: TFunction, audience: Audience): string {
  const worker = t('common:workerErrors.unknown');
  return audience === 'worker' ? worker : t('common:errors.unknown', { defaultValue: worker });
}

/** Rows 3–4: the transport code, or the generic sentence. */
function transportText(t: TFunction, code: string | undefined, audience: Audience): string {
  const fallback = generic(t, audience);
  if (!code) return fallback;
  if (TRANSPORT.has(code)) {
    if (audience === 'worker')
      return t(`common:workerErrors.${code}`, {
        defaultValue: t(`common:errors.${code}`, { defaultValue: fallback }),
      });
    return t(`common:errors.${code}`, {
      defaultValue: NEUTRAL_WORKER_TRANSPORT.has(code)
        ? t(`common:workerErrors.${code}`, { defaultValue: fallback })
        : fallback,
    });
  }
  // Row 2 for a code set by our own client code (e.g. the offline queue).
  return t(`common:workerErrors.${code}`, { defaultValue: fallback });
}

/**
 * Row 1 alone: the sentence for a prefixed business reason, or null when the
 * reason is not one of them (callers such as `addressErrorMessage` branch on it).
 */
export function reasonPrefixText(
  t: TFunction,
  reason: unknown,
  defaultValue?: string,
): string | null {
  const r = safe(reason);
  if (!r) return null;
  const row = REASON_ROWS.find((candidate) => candidate.test(r));
  if (!row) return null;
  return defaultValue === undefined ? t(row.key(r)) : t(row.key(r), { defaultValue });
}

function resolve(t: TFunction, err: unknown, audience: Audience): string {
  const details = shape(err).details ?? undefined;
  const reason = safe(details?.reason);
  const base = transportText(t, codeOf(err), audience);

  const prefixed = reasonPrefixText(t, reason, base);
  if (prefixed !== null) {
    // A worker sees the worker's own wording where one exists (ADDRESS_UNVERIFIED).
    return audience === 'worker'
      ? t(`common:workerErrors.${reason}`, { defaultValue: prefixed })
      : prefixed;
  }

  const detail = reason ?? safe(details?.conflict);
  if (detail) return t(`common:workerErrors.${detail}`, { defaultValue: base });
  return base;
}

/** A failure explained to a client user (portal, guest order, lists). Never `err.message`. */
export function clientErrorText(t: TFunction, err: unknown): string {
  return resolve(t, err, 'client');
}

/** A failure explained to a worker (worker portal, offline queue). Never `err.message`. */
export function workerErrorText(t: TFunction, err: unknown): string {
  return resolve(t, err, 'worker');
}
